/// A schematic of the two sextant scales — the arc (degrees) and the
/// micrometer drum (minutes) — with a pointer set to a given reading.
/// Intentionally simplified: the goal is the *skill* of reading two
/// scales, not "spot the index mirror." Used in the Reference tab as
/// an explainer and in the reading game with `showReading={false}`.

interface SextantFigureProps {
  /// Whole-degree reading on the arc, 0..89.
  degrees: number;
  /// Minutes on the micrometer drum, 0..59.9 (one-decimal resolution).
  minutes: number;
  /// When true, render the reading textually below the scales — useful
  /// in the Reference to label the example; hidden in the game.
  showReading?: boolean;
  size?: number;
}

const W = 400;
const H = 220;
const PAD = 20;
const TRACK_LEN = W - 2 * PAD; // 360
const ARC_Y = 60;
const DRUM_Y = 150;

function arcX(deg: number): number {
  return PAD + (deg / 90) * TRACK_LEN;
}

function drumX(min: number): number {
  return PAD + (min / 60) * TRACK_LEN;
}

function Pointer({ x, up }: { x: number; up: boolean }) {
  // Filled red triangle pointing at the scale. `up` flips it for the
  // micrometer scale (pointer below pointing up).
  const tip = up ? -8 : 8;
  return (
    <polygon
      points={`${x},0 ${x - 5},${tip} ${x + 5},${tip}`}
      fill="#c0382b"
    />
  );
}

export function SextantFigure({
  degrees,
  minutes,
  showReading = false,
  size = W,
}: SextantFigureProps) {
  // Build tick marks. Use refs to draw ticks at fixed positions; the
  // pointer x is computed from the prop.
  const arcMajor: number[] = [];
  const arcMinor: number[] = [];
  for (let d = 0; d <= 90; d += 5) {
    if (d % 10 === 0) arcMajor.push(d);
    else arcMinor.push(d);
  }
  const drumMajor: number[] = [];
  const drumMinor: number[] = [];
  for (let m = 0; m <= 60; m += 1) {
    if (m % 10 === 0) drumMajor.push(m);
    else drumMinor.push(m);
  }

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width={size}
      height={(size * H) / W}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={
        showReading
          ? `Sextant scales showing ${degrees}° ${minutes.toFixed(1)}'`
          : "Sextant scales — read the value"
      }
      style={{ maxWidth: "100%", height: "auto", display: "block" }}
    >
      {/* Arc (degrees) */}
      <text
        x={PAD}
        y={ARC_Y - 32}
        fontSize={12}
        fontWeight={600}
        fill="var(--text-muted, #64748b)"
      >
        Arc — degrees
      </text>
      <line
        x1={PAD}
        y1={ARC_Y}
        x2={PAD + TRACK_LEN}
        y2={ARC_Y}
        stroke="var(--text, #0f172a)"
        strokeWidth={1.5}
      />
      {arcMinor.map((d) => (
        <line
          key={`arcm-${d}`}
          x1={arcX(d)}
          y1={ARC_Y}
          x2={arcX(d)}
          y2={ARC_Y - 6}
          stroke="var(--text-muted, #64748b)"
          strokeWidth={0.8}
        />
      ))}
      {arcMajor.map((d) => (
        <g key={`arcM-${d}`}>
          <line
            x1={arcX(d)}
            y1={ARC_Y}
            x2={arcX(d)}
            y2={ARC_Y - 12}
            stroke="var(--text, #0f172a)"
            strokeWidth={1.2}
          />
          <text
            x={arcX(d)}
            y={ARC_Y - 16}
            fontSize={10}
            textAnchor="middle"
            fill="var(--text, #0f172a)"
          >
            {d}
          </text>
        </g>
      ))}
      {/* Pointer for the arc (above scale, pointing down). */}
      <g transform={`translate(0, ${ARC_Y - 12})`}>
        <Pointer x={arcX(degrees)} up={false} />
      </g>

      {/* Drum (minutes) */}
      <text
        x={PAD}
        y={DRUM_Y + 36}
        fontSize={12}
        fontWeight={600}
        fill="var(--text-muted, #64748b)"
      >
        Micrometer — minutes
      </text>
      <line
        x1={PAD}
        y1={DRUM_Y}
        x2={PAD + TRACK_LEN}
        y2={DRUM_Y}
        stroke="var(--text, #0f172a)"
        strokeWidth={1.5}
      />
      {drumMinor.map((m) => (
        <line
          key={`drumm-${m}`}
          x1={drumX(m)}
          y1={DRUM_Y}
          x2={drumX(m)}
          y2={DRUM_Y + 5}
          stroke="var(--text-muted, #64748b)"
          strokeWidth={0.6}
        />
      ))}
      {drumMajor.map((m) => (
        <g key={`drumM-${m}`}>
          <line
            x1={drumX(m)}
            y1={DRUM_Y}
            x2={drumX(m)}
            y2={DRUM_Y + 12}
            stroke="var(--text, #0f172a)"
            strokeWidth={1.2}
          />
          <text
            x={drumX(m)}
            y={DRUM_Y + 24}
            fontSize={10}
            textAnchor="middle"
            fill="var(--text, #0f172a)"
          >
            {m}
          </text>
        </g>
      ))}
      {/* Pointer for the drum (below scale, pointing up). */}
      <g transform={`translate(0, ${DRUM_Y + 12})`}>
        <Pointer x={drumX(minutes)} up />
      </g>

      {showReading && (
        <text
          x={W / 2}
          y={H - 6}
          textAnchor="middle"
          fontSize={14}
          fontWeight={700}
          fill="var(--text, #0f172a)"
        >
          Reads {Math.round(degrees)}° {minutes.toFixed(1)}′
        </text>
      )}
    </svg>
  );
}
